import {useQuery} from "@apollo/client";
import keyBy from "lodash/keyBy";
import {useEffect, useState} from "react";

import {ChecksContext} from "@/components/context/ChecksContext.tsx";
import {FilterCategory, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Sorting} from "@/components/table/Table.types.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GQL_CHECKS_QUERY2} from "@/lib/queries/checks.ts";
import {ServiceList} from "@/lib/types/checks.ts";
import {BulkActionsButtonBar} from "@/pages/checks/list/actions/BulkActionsButtonBar.tsx";
import {ChecksListViewsProvider} from "@/pages/checks/list/columns/ChecksListViewsContext.tsx";
import {useChecksListViews} from "@/pages/checks/list/columns/useChecksListViews.ts";
import {ChecksControlBar} from "@/pages/checks/list/components/ChecksControlBar.tsx";
import {ChecksSummaryBar} from "@/pages/checks/list/components/ChecksSummaryBar.tsx";
import {ChecksTable} from "@/pages/checks/list/components/ChecksTable.tsx";

const DEFAULT_SORT = "name";

export const CHECKS_STORAGE_KEYS = {
  DEFAULT: "checks_list_filters",
  GROUP: (groupId: string | number) => `checks_list_filters_group_${groupId}`,
} as const;

interface PageListProp {
  groupId?: string;
}

export const ChecksList = ({groupId}: PageListProp) => {
  const viewsMethods = useChecksListViews();
  const {isEnabled: viewsEnabled, isLoading: viewsLoading, activeView} = viewsMethods;

  const sessionStorageKey = groupId ? CHECKS_STORAGE_KEYS.GROUP(groupId) : CHECKS_STORAGE_KEYS.DEFAULT;

  const viewDefaultSort =
    viewsEnabled && activeView
      ? `${activeView.default_sort_direction === "desc" ? "-" : ""}${activeView.default_sort}`
      : DEFAULT_SORT;
  const {
    search,
    searchDebounced,
    searchChanged,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortingString,
    filterSets,
    setFilterSets,
  } = useTable({defaultSort: viewDefaultSort, sessionStorageKey});

  // A first visit waits for the Views, so the list loads once with the View's columns and sort. The wait ends in an
  // effect: the sort follows the View's default through an effect too, and both land in the same render.
  const [isWaitingForViews, setIsWaitingForViews] = useState(viewsLoading);
  useEffect(() => {
    if (!viewsLoading) {
      setIsWaitingForViews(false);
    }
  }, [viewsLoading]);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const {data, loading, previousData} = useQuery<ServiceList>(GQL_CHECKS_QUERY2, {
    skip: isWaitingForViews,
    variables: {
      ...pagination,
      sort: sortingString,
      filters: filterSets,
      search: searchDebounced,
      group_check_id: groupId,
    },
  });

  const renderData = loading ? previousData : data;

  if (!renderData) {
    return <LoadingContainer text="Loading Checks..." />;
  }

  const paginationChanged = (newPagination: LimitAndOffset) => {
    setPagination(newPagination);
    setRowSelection({});
  };

  const sortingChanged = (newSorting: Sorting) => {
    setSorting(newSorting);
    setRowSelection({});
  };

  const filterSetChanged = (newFilterSet: FilterSets<FilterCategory>) => {
    setFilterSets(newFilterSet);
    setRowSelection({});
  };

  const searchInputChanged = (newSearch: string) => {
    searchChanged(newSearch);
    setRowSelection({});
  };

  const services = renderData.services;

  return (
    <ChecksListViewsProvider {...viewsMethods}>
      <ChecksContext.Provider value={keyBy(services.items, "id")}>
        <ChecksSummaryBar stats={services.stats} filterSets={filterSets} onFilterSetChange={filterSetChanged} />
        <ChecksControlBar
          search={search}
          setSearch={searchInputChanged}
          filterSets={filterSets}
          onFilterSetChange={filterSetChanged}
        />
        <ChecksTable
          actionBar={
            <BulkActionsButtonBar
              selectedItemsIds={Object.keys(rowSelection)
                .filter((id) => rowSelection[id])
                .map((id) => services.items[parseInt(id)]?.id)
                .filter((id) => !!id)}
              onSuccess={() => setRowSelection({})}
            />
          }
          items={services.items}
          sortColumn={sorting.column}
          sortDirection={sorting.direction}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onSortingChange={sortingChanged}
          pagination={pagination}
          updatePagination={paginationChanged}
          totalRows={services.total}
          paginationData={services.items}
          isFiltered={!!groupId || !!search || Object.keys(filterSets).length > 0}
        />
      </ChecksContext.Provider>
    </ChecksListViewsProvider>
  );
};
